/**
 * LedgerSection — dashboard panel for Ledger accounts (name, group,
 * opening/closing balance). Has its own Company filter, same SectionFilter
 * component as the other sections. No Period filter here — the Ledger model
 * only stores current balances, not per-transaction dates, so a date range
 * would have no data to filter against.
 */

import React, { useEffect, useMemo, useState } from 'react';
import SectionFilter from './SectionFilter';
import LedgerExplorer from './LedgerExplorer';
import { useLedgersByCompany } from '../hooks/useDashboard';

const fmtCount = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

const LedgerSection = ({ companies }) => {
  const [companyId, setCompanyId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: ledgers, loading, error } = useLedgersByCompany(companyId, companies);
  const isAllCompanies = companyId === 'all';

  const filteredRows = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return ledgers || [];
    return (ledgers || []).filter((l) => String(l.name || '').toLowerCase().includes(q));
  }, [ledgers, searchTerm]);

  useEffect(() => {
    // Reset any states if necessary when company or search changes
  }, [companyId, searchTerm]);

  const handleReset = () => {
    setCompanyId('all');
    setSearchTerm('');
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Ledger</h2>
          <p className="panel-subtitle">
            {fmtCount(filteredRows.length)} ledger account(s){isAllCompanies ? ' across all companies' : ''}.
          </p>
        </div>
        <input
          type="search"
          className="txn-search"
          placeholder="Search ledger name…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <SectionFilter
        idPrefix="ledger"
        companies={companies}
        companyId={companyId}
        onCompanyChange={setCompanyId}
        onReset={handleReset}
        showPeriod={false}
      />

      <LedgerExplorer
        ledgers={filteredRows}
        showCompany={isAllCompanies}
      />
    </section>
  );
};

export default LedgerSection;
